/**
 * MaskEditor (002 US1/US2) — corrects or draws the print-area polygon + cutout
 * masks over the normalized case image. Supports vertex add/move/delete, cutout
 * editing, a draw-from-scratch mode (no AI proposal needed), and an effective-
 * print-area overlay (print area minus cutouts) previewed before lock.
 *
 * Background removal + print-area detection (approved MAGENTA flow):
 *  - Remove background (AI): the on-device BiRefNet matte isolates the product
 *    (clean edges on glass/clear cases), and the synthetic MAGENTA print-area
 *    marker baked into the generated image is keyed to recover the print region +
 *    a clean placement quad (engine.detectMagentaMarker → despilled product +
 *    print mask + quad). No colour picking. See detect/marker.rs + compositing.ts.
 *  - Preview mockup: composites design → shell (despilled product, print hole
 *    knocked out) over the chosen background via the engine. See render/mockup.rs.
 *  - Camera-cutout corner radius: round the active cutout's bounding box from a
 *    square → rounded square → circle by regenerating its polygon. See corners.ts.
 *
 * Editing + validation are CLIENT-SIDE (engine geometry). Only the submit/approve
 * transition is a cloud call (002 editor-approval). Coordinates are normalized
 * 0..1; the SVG maps them to the rendered case image box.
 */
import { useEffect, useMemo, useRef, useState } from "react";
// WS-J J-5: undo/redo/revert (and MN-5's per-editor history store) live in the bridge hook.
import { useEditorHistoryBridge } from "./useEditorHistoryBridge.js";
import type { CompositorInfo, Polygon, ValidationResult, SegmenterInfo, FitMode, VarnishParams } from "@genz/engine-bind";
// WS-J / AUD-2 RC-4: the pure pixel + colour helpers that used to live in this file's tail.
// Moved verbatim — same bodies, same JSDoc, same jsdom feature-detect guards. See `pixels.ts`.
import { get2dContext, rgbToHexColor } from "./pixels.js";
// WS-J J-6: the case image decode lifecycle, readSource and snapToEdges.
import { useCaseImageSource } from "./useCaseImageSource.js";
// WS-J J-3: the nine refs that ED-5 / ED-6 / F1 / A3 hand back and forth, in one explicit object.
import { useEditorRefs } from "./editorContext.js";
// WS-J J-8: compose/bake, the shared appearance-dep list and the imperative bake handle.
import { useMockupBake } from "./useMockupBake.js";
// WS-J J-9: the fill layers, ED-7's display-URL lifetime, the hosting window and the revert.
import { useLayerAssets, VARNISH_DEFAULT } from "./useLayerAssets.js";
// WS-J J-10: drags, vertex/cutout editing and ED-14's scoped arrow-key nudge. `DEFAULT_PRINT_AREA`
// and `rectFromDiagonal` travelled with them; the former is RE-EXPORTED below so the module's
// public surface is unchanged.
import { useMaskGeometry } from "./useMaskGeometry.js";
// WS-J J-11: the preview column (zoom bar, <svg> stage, handles, legend) as a pure component.
import { MaskCanvas } from "./MaskCanvas.js";
import { Accordion, type AccordionItem } from "../ui/Accordion.js";
import { Icon } from "../ui/Icon.js";
import { CutoutToolsCard } from "./controls/CutoutToolsCard.js";
import { BackgroundReplaceCard } from "./controls/BackgroundReplaceCard.js";
import { MarkerKeyingCard } from "./controls/MarkerKeyingCard.js";
import { DesignFitCard } from "./controls/DesignFitCard.js";
import { AiRemovalCard, defaultMatteQuality } from "./controls/AiRemovalCard.js";
import { HistoryCard } from "./controls/HistoryCard.js";
import { ProductProfileCard } from "./controls/ProductProfileCard.js";
export { DEFAULT_PRINT_AREA } from "./useMaskGeometry.js";
// WS-J J-12 — the component's public TYPE surface. Re-exported so its five importers (and the
// J-1 surface test) see exactly what they saw before.
export { defaultBrandRect, type MaskBakeHandle, type MaskEditorProps, type MatteKind } from "./maskEditorTypes.js";
import type { MaskEditorProps } from "./maskEditorTypes.js";
// WS-J J-7: the matte lifecycle (AI removal, chroma key, re-key, restore) and the process-global
// segmenter-model cache the editor's unmount cleanup releases.
import { useMatte } from "./useMatte.js";
// F-35 / J-14: the segmenter model outlives a remount.
import { releaseSegmentationModel, retainSegmentationModel } from "./segmentationModel.js";

import { type ProductCategory } from "./compositing.js";
import { BG_MODE_LABEL, FILL_FIT_LABEL, PRODUCT_CATEGORY_LABEL } from "./controlLabels.js";
import { guideKeyOptions, resolveCalibration } from "../stores/segmenterCalibration.js";
// WS-J J-4: zoom/pan state + ED-15's non-passive wheel listener.
import { useEditorZoom } from "./useEditorZoom.js";
import { baseTypeProfile } from "./baseType.js";
// WS-EXIF — the authoring-frame rule: whether this view's stored 0..1 geometry still means
// anything against the photo the editor decoded, and the arithmetic of the quarter-turn.
import {
  FRAME_MISMATCH_MESSAGE,
  frameMismatchDetail,
  frameVerdict,
  transposePolygon,
  transposePolygonBack,
} from "../templates/authoringFrame.js";
import { useObjectUrl } from "../generate/candidates.js";
import "./editor.css";



/** WS-EXIF — stable empty geometry for the blocked-frame case, so suppressing the quad does not
 *  hand `MaskCanvas` a new array identity on every render. */
const EMPTY_GEOMETRY: Polygon = [];
const EMPTY_CUTOUTS: MaskEditorProps["cutouts"] = [];

/** The sections a customer gets on a template made from Generate, in this order. */
const BASIC_RAIL = ["design-fit", "product"];

/**
 * WS-AUTOKEY — the rail's sections for the viewer (`MaskEditorProps.railMode`). A customer gets
 * Design fit and Product; an admin on a Generate-made template gets the same two and every other
 * section folded into one "Advanced (admin)" section; an admin in the case flow gets Design fit
 * first and the rest under one "Advanced" caption; every other host gets the list untouched.
 */
function railItemsFor(mode: MaskEditorProps["railMode"], items: AccordionItem[], adminItems: AccordionItem[] = []): AccordionItem[] {
  if (!mode) return items;
  if (mode === "advanced") {
    const fit = items.filter((it) => it.key === "design-fit");
    return [...fit, ...items.filter((it) => it.key !== "design-fit").map((it) => ({ ...it, group: "Advanced" }))];
  }
  const basic = BASIC_RAIL.flatMap((key) => items.filter((it) => it.key === key));
  if (mode === "placement") return basic;
  const rest = [...items.filter((it) => !BASIC_RAIL.includes(it.key)), ...adminItems];
  return [...basic, { key: "advanced-admin", head: "Advanced (admin)", body: <Accordion name="mask-rail-advanced" items={rest} /> }];
}

/** The head's trailing summary line. Plain text — `Accordion`'s `meta` slot is inside the
 *  `<summary>`, so anything interactive in here would toggle the section it describes. */
function railSummary(text: string) {
  return <span className="mask-editor__rail-summary">{text}</span>;
}

export function MaskEditor(props: MaskEditorProps) {
  const { engine, printArea, cutouts, onPrintAreaChange, onCutoutsChange, locked } = props;
  // WS-J J-3 — the nine refs `useMatte` / `useMockupBake` / `useLayerAssets` share, named once in
  // `editorContext.ts` instead of declared inline here. Same ref objects, same initial values;
  // only the owner changed. Their doc comments travelled with them.
  // Named `editorRefs`, not the plan's `refs`: the layer-refs restore closures below already bind a
  // local `refs` (`props.initialLayerRefs`), and shadowing it there was a typecheck error.
  const editorRefs = useEditorRefs();
  /** Short status line for the placement detector (kind + coverage, or "no region"). */
  const [placementMsg, setPlacementMsg] = useState<string | null>(null);
  /**
   * WS-J J-6 — the case image's decode lifecycle (`useCaseImageSource`): natDims, chromaReady, the
   * loader effect, `readSource` and `snapToEdges`. The half of the per-image reset that belongs to
   * the matte / marker / display state is handed back through `sourceResetRef`, which is assigned
   * further down once those refs exist — the same late-bound-ref pattern `applyMatteToSourceRef`
   * and `resetBackgroundRef` already use. J-7 moves that body into `useMatte`.
   */
  const sourceResetRef = useRef<(() => void) | null>(null);
  const {
    width: w,
    height: h,
    // WS-EXIF: the DECODED size, which is null until the photo loads — unlike `w`/`h`, which
    // fall back to 512. The frame check must not accuse a photo that has not arrived yet.
    natDims,
    chromaReady,
    caseImageFailedRef,
    originalBytesRef,
    pristineRgbaRef,
    readSource,
    snapToEdges,
  } = useCaseImageSource({
    url: props.caseImageUrl,
    fallbackWidth: props.width,
    fallbackHeight: props.height,
    sourceRef: editorRefs.sourceRef,
    onNaturalDims: props.onNaturalDims,
    onLoadError: props.onCaseImageError,
    setPlacementMsg,
    logger: props.logger,
    onSourceResetRef: sourceResetRef,
  });
  // F-35 / J-14 — free the on-device segmenter session when the editor CLOSES, so the app's RAM
  // returns to baseline once the user is done with the AI matte (the model + ORT arena held ~GBs →
  // the "6.5 GB still held" the user saw). This used to free it on EVERY unmount, and
  // `TemplateEditor` remounted the editor on every view-tab switch, Force sync, view removal and
  // the auto-`openDraft` — so it threw away a ~109 MB model four different ways and reloaded it
  // ~12 s later. It is now a refcount plus a 30 s grace window: a remount inside the window keeps
  // the model, a genuine close still frees it. See `editor/segmentationModel.ts`.
  useEffect(() => {
    retainSegmentationModel();
    return () => releaseSegmentationModel(props.engine);
  }, [props.engine]);
  // Per-image AI matte quality: "quality" = the platform default (BiRefNet, cleanest, ~12s CPU),
  // "fast" = U²-Net-p (~1s, lighter) for when the ~12s pass is too slow. Doesn't touch the admin
  // platform default; just picks the model for THIS removal.
  const [matteQuality, setMatteQuality] = useState<"fast" | "quality">(defaultMatteQuality);
  /** On-device segmenter diagnostics (CPU/GPU, model loaded, last error) — desktop only. */
  const [segInfo, setSegInfo] = useState<SegmenterInfo | null>(null);
  const [compInfo, setCompInfo] = useState<CompositorInfo | null>(null);
  /** Blend mode for the TEXTURE overlay layer (normal|multiply|screen|overlay|softlight|
   *  uvvarnish). "uvvarnish" = selective spot-UV emboss (see `varnish` calibration). */
  const [textureBlend, setTextureBlend] = useState<string>("normal");
  /** Spot-UV varnish calibration used when `textureBlend === "uvvarnish"`. Its own section of
   *  sliders (replaces the texture opacity row for a varnish layer). Saved with the template. */
  const [varnish, setVarnish] = useState<VarnishParams>({ ...VARNISH_DEFAULT });
  /** UV varnish calibration panel is collapsed by default — opened via the settings (gear) icon. */
  const [showVarnishSettings, setShowVarnishSettings] = useState<boolean>(false);
  /** "Rotate placement" — the print-area design/texture is rotated by this many degrees
   *  (0/90/180/270), cycled 90° per button press. Saved with the template (baked_assets). */
  const [designRotation, setDesignRotation] = useState<number>(0);
  /** Fill mode for the design within the print area (contain/cover/stretch/height/width).
   *  Saved with the template (baked_assets). Default "stretch" applies to NEW templates only
   *  (mounted without initialLayerRefs); the reopen restore explicitly re-seeds "height" for an
   *  EXISTING template whose refs predate design_fill, so legacy templates keep their old
   *  behaviour (see the layer-restore effect + the engine-bind `?? "height"` fallbacks). */
  const [fillFit, setFillFit] = useState<FitMode>("stretch");
  /** Forced design aspect [w,h] (center-crop); null = original. Saved with the template. */
  const [designAspect, setDesignAspect] = useState<[number, number] | null>(null);
  /** Opt-in "AI edge": after a removal, build the print mask from the AI MATTE (content-aware —
   *  the flat panel the matte removed = print area; the camera/edges it kept = protected) instead
   *  of the chroma tolerance. Kills the colour-spill rim + the over/under-key. Needs a removal
   *  first (matteAlphaRef) + Protect product on; falls back to chroma otherwise. */
  const [aiEdge, setAiEdge] = useState(false);
  /** Advanced AI-edge tuning (0..100): how much partial/soft-boundary content the matte removed
   *  to PROTECT (keep visible) rather than key away. 0 = default (matches the plain AI-edge);
   *  raise it if AI edge is hiding thin content near the panel edge. */
  const [aiEdgeKeep, setAiEdgeKeep] = useState(0);
  const [validation, setValidation] = useState<ValidationResult>({ valid: true, errors: [] });
  const [effective, setEffective] = useState<Polygon>(printArea);
  /**
   * WS-EXIF — the operator has answered the frame-mismatch banner (re-fitted, or kept the quad
   * as it is), so the canvas may draw the print area again.
   *
   * Reset per VIEW and per PHOTO below: the answer is about one view's geometry against one
   * image, and carrying it to the next tab would hide a second, real mismatch.
   */
  const [frameResolved, setFrameResolved] = useState(false);
  // #3 — Canvas zoom (1..MAX_ZOOM) + the normalized point the view is centered on, so the
  // 4 corners can be placed precisely. WS-J J-4: the state, the derived viewBox/handle sizes and
  // ED-15's non-passive wheel listener now live in `useEditorZoom`; `svgRef` travelled with them
  // because that listener attaches to the SVG's stage parent.
  const { zoom, setZoom, center, setCenter, vb, hr, hitr, sw, zoomBy, resetZoom, svgRef } =
    useEditorZoom({ width: w, height: h });

  // Per-template edge-calibration sliders (B3): the AI-matte cutout's feather / choke /
  // matte-strength, SEEDED from the platform-global calibration (super-admin's spec 022
  // defaults) but tunable HERE per template so the founder can calibrate the AI edge without
  // editing global settings. Applied live in applyMatteToSource (choke/feather/strength) +
  // previewMockup (feather softens the print-area edge). Seeded from the global calibration:
  // choke = edge_choke_px (2), feather = edge_feather_px (now 1px by default — softens the AI
  // cutout edge), strength = 0. The per-template sliders override these per template.
  const [edgeChoke, setEdgeChoke] = useState(() => resolveCalibration().edge_choke_px);
  const [edgeFeather, setEdgeFeather] = useState(() => resolveCalibration().edge_feather_px);
  const [matteStrength, setMatteStrength] = useState(0);
  // Artwork-area shading: multiply the case's own print-region brightness over the placed design so
  // it reads as shaded under the shell instead of a flat sticker. 0 = flat. Default 0.6 (shadows on).
  const [printShadingStrength, setPrintShadingStrength] = useState(() => resolveCalibration().print_shading);
  // Synthetic light for optically-FLAT marker panels (an AI-generated flat-magenta base records no
  // body light to transfer): a vertical top-light ramp, a subtle print grain and a gloss sheen,
  // layered into the engine ShadingParams. 0 = off for all three.
  //
  // Seeded from PRINT_LIGHT_DEFAULTS, which is ALSO what the card's "Reference" preset is built
  // from — one constant, so a retune can never leave a new template opening with no preset lit
  // (review-1 F2). The calibration rationale for the three numbers lives on that constant; retune
  // it there, not here.
  // `initialPrintLight="flat"` seeds the Flat finish instead. A seed only: a saved calibration
  // still wins in `useLayerAssets`, exactly as it does over the Reference seed.
  const flatLight = props.initialPrintLight === "flat";
  const [shadeGradient, setShadeGradient] = useState(() => flatLight ? 0 : resolveCalibration().print_gradient);
  const [shadeGrain, setShadeGrain] = useState(() => flatLight ? 0 : resolveCalibration().print_grain);
  const [shadeSheen, setShadeSheen] = useState(() => flatLight ? 0 : resolveCalibration().print_sheen);
  // Additive highlights (WS-HL) — see `PRINT_LIGHT_DEFAULTS`. This seed is the NEW-template
  // default; an EXISTING template's saved calibration overrides it in `useLayerAssets` (to 0
  // when it predates the field), so reopening an old template never lights it by surprise.
  const [shadeHighlights, setShadeHighlights] = useState(() => flatLight ? 0 : resolveCalibration().print_highlights);
  // How deep the case's recorded shadows and highlights sit over the artwork (owner, 2026-10-12).
  // A new template opens at the platform calibration a super-admin set (Admin → AI matte),
  // the same for every customer; a saved one restores its own in `useLayerAssets`.
  // The print guide key's options, tried here and saved for everyone from Marker & keying.
  const [guideKey, setGuideKey] = useState(guideKeyOptions);
  const [shadeShadowDepth, setShadeShadowDepth] = useState(() => resolveCalibration().key_shadow_depth);
  const [shadeHighlightDepth, setShadeHighlightDepth] = useState(() => resolveCalibration().key_highlight_depth);
  // The design's ink range: a real print has no pure black and no pure white (owner, 2026-10-14).
  const [shadeInkBlack, setShadeInkBlack] = useState(() => resolveCalibration().print_black);
  const [shadeInkWhite, setShadeInkWhite] = useState(() => resolveCalibration().print_white);
  // "AI settings" gear popup (mirrors the UV settings popup); collapsed by default.
  /* The rail's open section, held by the PAGE when there is one (fix round 1, F-5).
     `defaultOpen` is read once at mount, so it cannot survive anything — but neither could a
     `useState` in here, and that was the earlier claim's mistake: `TemplateEditor` remounts
     THIS COMPONENT by `key` whenever `forceSyncEpoch` or `prunedEpoch` moves (a forced
     re-download, a view tombstone), so state held here went with it. Moving it from the
     primitive to the component that is remounted bought nothing; it has to sit above the
     `key`, which is the page. `railOpenKey`/`onRailOpenChange` is that lift.
     The fallback below is for a caller with no epochs of its own (the suites, any standalone
     embed): it seeds the key once, here, and nothing remounts it.
     `null` is legitimate either way: closing the open section leaves the canvas the column. */
  // WS-AUTOKEY: the wizard's two rails open on placement — the magenta key already ran by itself.
  const [ownRailOpen, setOwnRailOpen] = useState<string | null>(props.railMode ? "design-fit" : "bg-removal");
  const railOpen = props.railOpenKey === undefined ? ownRailOpen : props.railOpenKey;
  const setRailOpen = props.onRailOpenChange ?? setOwnRailOpen;
  const [showAiSettings, setShowAiSettings] = useState(false);
  // Per-stage show/hide of the removal pipeline — turning one OFF makes that stage a no-op, applied
  // live. Persisted so a template keeps its calibrated pipeline.
  const [stageDespill, setStageDespill] = useState(true);
  const [stageRefine, setStageRefine] = useState(true); // edge choke + feather
  const [stageMatteStrength, setStageMatteStrength] = useState(true);
  const [stageShading, setStageShading] = useState(true); // artwork shading
  // The on-device AI matte (BiRefNet) is only present in the native/desktop build; the
  // web/WASM binding omits removeBackgroundAI. Gate the Remove action on its availability
  // so web doesn't error on every Remove.
  const aiAvailable = !!engine.removeBackgroundAI;
  // True while an AI matte is running (incl. the one-time model download), so the Remove
  // action disables + shows progress instead of silently blocking.
  const [aiBusy, setAiBusy] = useState(false);
  /** Elapsed seconds while the on-device AI matte runs (a single ONNX pass has no true %,
   *  so we show honest elapsed time + a pulse bar instead of a silent "Working…"). */
  const [aiElapsed, setAiElapsed] = useState(0);
  /** Marker COLOUR override. `null` = the engine's default magenta keying; set it via the
   *  eyedropper/picker when the generated marker drifted from the #FF00FF code so keying
   *  targets the picked colour instead. The rest of the flow is unchanged. */
  const [markerColor, setMarkerColor] = useState<[number, number, number] | null>(null);
  /** Eyedropper active → the next image click samples the marker colour. */
  const [markerEyedrop, setMarkerEyedrop] = useState(false);
  /** Picked-colour tolerance (0..255 sensitivity; 0 = engine default). */
  const [markerTol, setMarkerTol] = useState(0);
  /** Transient feedback for the marker eyedropper — the pick used to silently reset the
   *  swatch (near-magenta → default keyer) with zero explanation. Auto-clears after ~3s. */
  const [pickNotice, setPickNotice] = useState<string | null>(null);
  const pickNoticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Show a transient marker-pick notice; replaces any prior one and auto-clears after ~3s. */
  const flashPickNotice = (msg: string) => {
    if (pickNoticeTimer.current) clearTimeout(pickNoticeTimer.current);
    setPickNotice(msg);
    pickNoticeTimer.current = setTimeout(() => setPickNotice(null), 3000);
  };
  /**
   * WS-J J-10 / AUD-2 RC-4 — pointer drags, vertex editing, cutout shaping and ED-14's
   * focus-or-pointer-scoped arrow-key nudge. Moved verbatim into `useMaskGeometry.ts`.
   */
  const {
    rootRef, pointerInsideRef,
    drag, setDrag,
    printAreaActive, setPrintAreaActive,
    activeCutout, setActiveCutout,
    selectedPrintVertex, setSelectedPrintVertex,
    toNorm, onPointerMove, endDrag,
    onBackgroundClick,
    deleteVertex, addCutout, deleteActiveCutout, seedPrintArea, addShapeCutout,
    smoothActiveCutout, adjustCornerRadius,
  } = useMaskGeometry({
    locked,
    inactive: props.inactive,
    placementOnly: props.railMode === "placement",
    logger: props.logger,
    printArea,
    cutouts,
    brandArea: props.brandArea,
    onPrintAreaChange,
    onCutoutsChange,
    onBrandAreaChange: props.onBrandAreaChange,
    w, h, vb, svgRef, setCenter,
    readSource,
    markerEyedrop,
    setMarkerEyedrop,
    setMarkerColor,
    flashPickNotice,
  });
  useEffect(() => () => { if (pickNoticeTimer.current) clearTimeout(pickNoticeTimer.current); }, []);
  /** Spec 019 US3: product profile (category + clear sub-toggle). Drives the fill stack
   *  (phone layer only for a clear phone case); shown in the right-side control panel. */
  const [profileCategory, setProfileCategory] = useState<ProductCategory>(
    () => baseTypeProfile(props.baseType).category,
  );
  const [profileClear, setProfileClear] = useState(() => baseTypeProfile(props.baseType).clear);
  // #6 — re-seed the product profile when the case base type CHANGES (glass → clear/glossy,
  // TPU → opaque) so the baked mockup composites per material. The operator can still override
  // the profile afterward.
  //
  // P1-3/P1-4: the mount run is SKIPPED. The two `useState` initializers above already seeded
  // from exactly this base type, so the mount run was a pure no-op re-set — except that the
  // parent can re-render with an identical-by-value base type at any time (TemplateEditor
  // refreshes `detailBaseType` from the refetched template), and running it after the saved
  // profile has been restored would silently CLOBBER the persisted category / clear flag back to
  // the material default. Only a genuine change re-seeds.
  const seededBaseTypeRef = useRef(props.baseType);
  useEffect(() => {
    if (seededBaseTypeRef.current === props.baseType) return;
    seededBaseTypeRef.current = props.baseType;
    const p = baseTypeProfile(props.baseType);
    setProfileCategory(p.category);
    setProfileClear(p.clear);
  }, [props.baseType]);
  /** Layer visibility (US3): include a texture/effect layer in the fill stack. UV cases
   *  always include it. Reorder is fixed by physics (phone behind design behind shell). */
  const [profileTexture, setProfileTexture] = useState(false);
  /** Per-layer opacity 0..1 (US3 Layers). Baked into the mockup assets so the studio
   *  render composites each fill layer at this opacity. */
  const [layerOpacity, setLayerOpacity] = useState<{ phone: number; design: number; texture: number }>(
    { phone: 1, design: 1, texture: 1 },
  );
  /** Spec 019 US3 background-tool control: keep the print-area region during Auto
   *  background so the segmenter/classical remover doesn't eat the product/design. */
  const [protectProduct, setProtectProduct] = useState(true);
  /** Whether the chroma color has been baked to transparency on the source. */
  const [removed, setRemoved] = useState(false);
  // --- Background REPLACEMENT (non-destructive): once the bg is removed (mask),
  // composite the foreground over a chosen background for the displayed/used image.
  type BgMode = "transparent" | "color" | "image";
  // The preview backdrop (never baked): the template's clear image when the host has one, so an
  // empty print area shows the phone through the clear case; otherwise light grey, never white
  // (owner, 2026-10-15) and never a transparent checkerboard.
  const [bgMode, setBgMode] = useState<BgMode>("color");
  const [bgColor, setBgColor] = useState("#e8e8e8");
  const [blendMode, setBlendMode] = useState<GlobalCompositeOperation>("source-over");
  /** Uploaded/template background image (drawn behind the foreground). */
  const bgImageRef = useRef<HTMLImageElement | null>(null);
  // previewMockup reads the layer bytes from these REFS, not the state: a handler sets state
  // and immediately calls previewMockup in the same tick, so the captured state would still be
  // the stale pre-update value (the just-picked layer would be missing from the first preview).
  // The apply* helpers keep ref + state in lockstep; every layer mutation must go through them.
  const testDesignRef = useRef<Uint8Array | null>(null);
  const clearBgRef = useRef<Uint8Array | null>(null);
  const alignedClearRef = useRef<Uint8Array | null>(null);
  const textureRef = useRef<Uint8Array | null>(null);
  // Hosted SOURCE url of a library-picked layer (null for a custom local upload). Persisted
  // into baked_assets so the layer restores when the template opens on another system.
  const clearBgUrlRef = useRef<string | null>(null);
  const textureUrlRef = useRef<string | null>(null);
  /** Bumped on each removal so the recomposite effect re-runs (even re-removals). */
  const [removalVer, setRemovalVer] = useState(0);
  // The host's backdrop image, loaded once per URL; a failed load keeps the grey backdrop. It
  // leaves with its URL (another view), unless a background image was picked since.
  useEffect(() => {
    const url = props.previewBackdropUrl;
    if (!url || typeof Image === "undefined") return;
    let live = true;
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => {
      if (!live) return;
      bgImageRef.current = im;
      setBgMode("image");
      setRemovalVer((v) => v + 1);
    };
    im.src = url;
    return () => {
      live = false;
      if (bgImageRef.current !== im) return;
      bgImageRef.current = null;
      setBgMode("color");
      setRemovalVer((v) => v + 1);
    };
  }, [props.previewBackdropUrl]);
  // --- Geometry undo / redo / reset (spec 019 US4). WS-J J-5: the seed/commit effects, the ED-9
  // quiet window, ED-8's stashed matte, ED-1's rebase and the step/undo/redo/revert helpers all
  // live in `useEditorHistoryBridge` now — on a store that belongs to THIS editor (MN-5).
  const {
    canUndo,
    canRedo,
    canRevert,
    undoGeo,
    redoGeo,
    revertGeo,
    pendingHistoryTargetRef,
    restoreRemovalRef,
    undoneMatteRef,
    undoneMatteKindRef,
    resetBackgroundRef,
    applyMatteToSourceRef,
  } = useEditorHistoryBridge({
    printArea,
    cutouts,
    brandArea: props.brandArea,
    removed,
    dragging: !!drag,
    locked,
    caseImageUrl: props.caseImageUrl,
    view: props.view,
    onPrintAreaChange,
    onCutoutsChange,
    onBrandAreaChange: props.onBrandAreaChange,
    matteAlphaRef: editorRefs.matteAlphaRef,
    setPlacementMsg,
    logger: props.logger,
  });
  /** Always points at the latest `previewMockup` closure so the (stable) imperative handle
   *  invokes the current one (previewMockup is redefined every render; the handle is not).
   *  It resolves `true` iff the call emitted `onBaked`, so `bakeNow()` can report the emit. */
  const previewMockupRef = useRef<(uploadBake?: boolean) => Promise<boolean>>(() => Promise.resolve(false));
  /** ED-7 — `setDisplayImage` is owned by `useLayerAssets` (J-9), which is called after the
   *  compose hook that repaints through it. Same late-bound-ref pattern as `previewMockupRef`. */
  const setDisplayImageRef = useRef<(url: string | undefined, isObjectUrl?: boolean) => void>(() => {});
  /**
   * WS-J J-7 / AUD-2 RC-4 — the matte lifecycle: `autoRemoveBg` and its ED-9 cancel/stale
   * machinery, `applyMatteToSource` (the US3 protect, the B3 strength remap, the F1 calibration
   * override, the F6 scratch reuse and the CHROMA branch that is WS-A's byte-identity contract),
   * `rekeyChroma`, `printAlphaFor`, `reapplyMatte`, the "Keyed %" readout, the B5 matte-only
   * re-key debounce and `runMatteRestore`'s failure latch. Moved verbatim into `useMatte.ts`;
   * every comment travelled with the line it explains.
   */
  const {
    markerRef,
    matteKindRef,
    matteClearedRef,
    autoRemoveReqRef,
    reapplyMatteRef,
    restoredMatteRef,
    matteRestoreFailedRef,
    setMatteRestoreFailed,
    printAlphaFor,
    autoRemoveBg,
    cancelAutoRemoveBg,
    removeByMagentaKey,
    autoMagentaKey,
    retryMatteRestore,
    keyedPct,
    matteRestoreFailed,
    matteRestoring,
  } = useMatte({
    engine,
    shadedMagentaGuide: props.shadedMagentaGuide ?? props.initialLayerRefs?.shadedMagentaGuide,
    guideKey,
    locked,
    logger: props.logger,
    versionId: props.versionId,
    onBaked: props.onBaked,
    initialLayerRefs: props.initialLayerRefs,
    editorRefs,
    view: props.view,
    chromaReady,
    readSource,
    snapToEdges,
    pristineRgbaRef,
    originalBytesRef,
    width: w,
    height: h,
    printArea,
    onPrintAreaChange,
    aiBusy,
    setAiBusy,
    matteQuality,
    markerColor,
    markerTol,
    protectProduct,
    removed,
    setRemoved,
    setRemovalVer,
    setPlacementMsg,
    edgeChoke,
    edgeFeather,
    matteStrength,
    stageDespill,
    stageRefine,
    stageMatteStrength,
    setEdgeChoke,
    setEdgeFeather,
    setMatteStrength,
    aiEdge,
    setAiEdge,
    aiEdgeKeep,
    setAiEdgeKeep,
    testDesignRef,
    previewMockupRef,
    restoreRemovalRef,
    undoneMatteKindRef,
    applyMatteToSourceRef,
    coverageHeightPct: props.coverageHeightPct === undefined ? 95 : props.coverageHeightPct,
  });

  /* WS-AUTOKEY — the automatic magenta key. Once per image: the ref holds the url the verdict
     was reached for, so a re-render (or the parent's print-area write the key itself causes)
     never keys twice, and a NEW image keys again. A `"skipped"` run records nothing, so the next
     render after the canvas becomes readable tries again. `quiet` in the placement-only rail:
     the admin refusal sentence names an eyedropper that rail does not have, and the host says
     "could not be found" in its own words. */
  const autoKeyedUrlRef = useRef<string | undefined>(undefined);
  // The key and the callback are fresh closures every render; reading them through refs keeps
  // them out of the dependency list, so the list names only what should re-arm the key.
  const onAutoMagentaKeyRef = useRef(props.onAutoMagentaKey);
  onAutoMagentaKeyRef.current = props.onAutoMagentaKey;
  const autoMagentaKeyRef = useRef(autoMagentaKey);
  autoMagentaKeyRef.current = autoMagentaKey;
  const { autoMagentaKey: autoKeyOn, caseImageUrl } = props;
  const quietAutoKey = props.railMode === "placement";
  useEffect(() => {
    if (!autoKeyOn || !chromaReady) return;
    if (autoKeyedUrlRef.current === caseImageUrl) return;
    // A LOCKED version is refused inside `keyMagenta` ("skipped", nothing recorded) — ONE guard,
    // shared with the button, so the review's mutation F10 has a single line to hit and a test
    // that sees it. `locked` stays in the list below so an unlock re-arms the key.
    const { verdict, reason } = autoMagentaKeyRef.current(quietAutoKey);
    if (verdict === "skipped") return;
    autoKeyedUrlRef.current = caseImageUrl;
    onAutoMagentaKeyRef.current?.(verdict, reason);
  }, [autoKeyOn, chromaReady, locked, caseImageUrl, quietAutoKey]);

  // Segmenter diagnostics: fetch on open + whenever a removal finishes (aiBusy flips), so the
  // CPU/GPU + model-loaded + last-error line reflects the real native state.
  useEffect(() => {
    if (!engine.segmenterInfo) { setSegInfo(null); return; }
    let alive = true;
    void engine.segmenterInfo().then((i) => { if (alive) setSegInfo(i); }).catch(() => { /* best-effort */ });
    return () => { alive = false; };
  }, [engine, aiBusy]);

  // Compositor diagnostics (spec 024) — same cadence as segInfo (open + aiBusy flips), NEVER
  // per-render: the native call triggers wgpu adapter init on first touch, so it must be a
  // deliberate, occasional fetch off the render path.
  useEffect(() => {
    if (!engine.compositorInfo) { setCompInfo(null); return; }
    let alive = true;
    void engine.compositorInfo().then((i) => { if (alive) setCompInfo(i); }).catch(() => { /* best-effort */ });
    return () => { alive = false; };
  }, [engine, aiBusy]);

  // Elapsed-seconds ticker while the on-device AI matte runs (UX: never a silent wait).
  useEffect(() => {
    if (!aiBusy) {
      setAiElapsed(0);
      return;
    }
    const start = Date.now();
    const t = setInterval(() => setAiElapsed(Math.max(1, Math.round((Date.now() - start) / 1000))), 250);
    return () => clearInterval(t);
  }, [aiBusy]);

  // Re-validate + recompute effective area whenever geometry changes.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [v, eff] = await Promise.all([
        engine.geometryValidate(printArea, cutouts),
        engine.effectivePrintArea(printArea, cutouts),
      ]);
      if (!cancelled) {
        setValidation(v);
        setEffective(eff);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [engine, printArea, cutouts]);

  // WS-J J-6 — the half of the per-image reset that belongs to the matte, the marker cache and the
  // display URL. It moved out of `useCaseImageSource`'s loader effect verbatim and is handed back
  // through `sourceResetRef` because these refs are declared below the hook call. The effect runs
  // after render, so the assignment is always in place by the time it fires. Every line, and every
  // comment explaining a line, is the one the loader effect used to run inline.
  sourceResetRef.current = () => {
    setRemoved(false);
    setRemovalVer(0);
    setDisplayImage(undefined);
    markerRef.current = null;
    editorRefs.matteAlphaRef.current = null;
    matteKindRef.current = null;
    // A new case image is NOT a deliberate clear — the saved matte (if any) should still restore.
    matteClearedRef.current = false;
    // A new case image → re-arm the saved-matte restore so its removed state is replayed once
    // the new source is ready (the guard is one-shot per image). Clear the failure latch too so a
    // prior image's restore failure doesn't suppress the new image's restore attempt, and clear
    // the FAILURE BANNER STATE too — otherwise a stale "restore failed" banner from the previous
    // image lingers even after swapping to an image whose refs have no matteUrl at all.
    restoredMatteRef.current = false;
    matteRestoreFailedRef.current = false;
    setMatteRestoreFailed(false);
    // The source image is changing — invalidate any in-flight auto-clean so its (now stale)
    // matte can't paint onto the new image when it finally resolves.
    autoRemoveReqRef.current += 1;
  };
  /**
   * WS-J J-8 / AUD-2 RC-4 — compose, bake, persist, and the three dependency lists that must not
   * drift: `composeMockupOnce` / `previewMockup`, the SHARED `appearanceDeps`, P3-1's
   * `backdropPreviewDeps`, the A3 bake-dirty effect (ED-5's conditional clean, F1's consume-once,
   * the mount skip), the imperative `MaskBakeHandle`, and the debounced live-preview effect.
   * Moved verbatim into `useMockupBake.ts`; every comment travelled with the line it explains.
   */
  const { previewMockup, aiStatus, setAiStatus } = useMockupBake({
    engine,
    shadedMagentaGuide: props.shadedMagentaGuide ?? props.initialLayerRefs?.shadedMagentaGuide,
    locked,
    logger: props.logger,
    versionId: props.versionId,
    view: props.view,
    onBaked: props.onBaked,
    bakeRef: props.bakeRef,
    editorRefs,
    chromaReady,
    readSource,
    originalBytesRef,
    caseImageFailedRef,
    markerRef,
    matteKindRef,
    matteClearedRef,
    printAlphaFor,
    reapplyMatteRef,
    removed,
    testDesignRef,
    clearBgRef,
    alignedClearRef,
    textureRef,
    clearBgUrlRef,
    textureUrlRef,
    bgImageRef,
    setDisplayImageRef,
    previewMockupRef,
    setPlacementMsg,
    printArea,
    markerColor,
    markerTol,
    textureBlend,
    varnish,
    designRotation,
    layerOpacity,
    profileCategory,
    profileClear,
    profileTexture,
    aiEdge,
    aiEdgeKeep,
    fillFit,
    designAspect,
    edgeChoke,
    edgeFeather,
    matteStrength,
    stageDespill,
    stageRefine,
    stageMatteStrength,
    stageShading,
    printShadingStrength,
    shadeGradient,
    shadeGrain,
    shadeSheen,
    shadeHighlights,
    shadeShadowDepth,
    shadeHighlightDepth,
    shadeInkBlack,
    shadeInkWhite,
    guideKey,
    matteQuality,
    protectProduct,
    bgMode,
    bgColor,
    blendMode,
    templateBackgroundColor: props.templateBackgroundColor,
  });


  /**
   * WS-J J-9 / AUD-2 RC-4 — the three fill layers, ED-7's display-URL lifetime, the upload
   * handlers and ED-6's hosting window, `pickImageFromUrl`, `resetBackground`, and the layer-refs
   * restore effect. Moved verbatim into `useLayerAssets.ts`.
   *
   * Called HERE (after `useMatte` and `useMockupBake`) because `resetBackground` reads their refs
   * and `setAiStatus`. The five layer byte refs and `bgImageRef` therefore stay declared above —
   * J-7/J-8 read them first — and `setDisplayImage` is published back through `setDisplayImageRef`.
   */
  const {
    testDesign, testDesignName, testDesignInputRef,
    clearBg, clearBgName, setClearBgName, clearBgInputRef,
    texture, textureName, setTextureName, textureInputRef,
    applyClearBg, applyTexture,
    displayUrl, setDisplayImage,
    onBgImageFile, onTestDesignFile, clearTestDesignNow, onProductImageFile, pickImageFromUrl,
    resetBackground,
  } = useLayerAssets({
    locked,
    logger: props.logger,
    templateId: props.templateId,
    caseImageUrl: props.caseImageUrl,
    onUploadImage: props.onUploadImage,
    initialLayerRefs: props.initialLayerRefs,
    editorRefs,
    view: props.view,
    testDesignRef, clearBgRef, textureRef, clearBgUrlRef, textureUrlRef, bgImageRef,
    previewMockupRef, setDisplayImageRef,
    pendingHistoryTargetRef, undoneMatteRef, undoneMatteKindRef, resetBackgroundRef,
    matteKindRef, matteClearedRef, setAiStatus,
    setPlacementMsg, setRemoved, setRemovalVer,
    textureBlend, varnish, designRotation, fillFit, designAspect, layerOpacity,
    profileCategory, profileClear, profileTexture, printShadingStrength,
    shadeGradient, shadeGrain, shadeSheen, shadeHighlights, shadeShadowDepth, shadeHighlightDepth,
    shadeInkBlack, shadeInkWhite,
    stageDespill, stageRefine, stageMatteStrength, stageShading,
    matteQuality, markerColor, markerTol, protectProduct,
    setTextureBlend, setVarnish, setDesignRotation, setFillFit, setDesignAspect, setLayerOpacity,
    setProfileCategory, setProfileClear, setProfileTexture, setPrintShadingStrength,
    setShadeGradient, setShadeGrain, setShadeSheen, setShadeHighlights,
    setShadeShadowDepth, setShadeHighlightDepth, setShadeInkBlack, setShadeInkWhite,
    setStageDespill, setStageRefine, setStageMatteStrength, setStageShading,
    setMatteQuality, setMarkerColor, setMarkerTol, setProtectProduct, setBgMode, setAiEdge,
  });


  const clearUnderlay = useMemo(
    () => (profileCategory === "phone_case" && profileClear && clearBg ? { bytes: clearBg } : undefined),
    [profileCategory, profileClear, clearBg],
  );
  const clearUnderlayUrl = useObjectUrl(clearUnderlay);

  const { clearCaseImage } = props;
  useEffect(() => {
    if (!clearCaseImage) return;
    alignedClearRef.current = clearCaseImage;
    clearBgUrlRef.current = null;
    applyClearBg(clearCaseImage);
    setClearBgName("Clear case from Generate");
    setProfileClear(true);
  }, [clearCaseImage, applyClearBg, setClearBgName]);

  /** What the canvas is showing right now, readable from the effect below WITHOUT becoming one
   *  of its dependencies — that effect writes `displayUrl`, so depending on it would loop. */
  const displayUrlRef = useRef<string | undefined>(undefined);
  displayUrlRef.current = displayUrl;

  // Recomposite the displayed image = foreground (the removed image, with its
  // transparency) over the chosen background, using the blend mode. Non-destructive:
  // sourceRef (used for detection) is untouched; only the DISPLAY changes.
  useEffect(() => {
    if (removalVer === 0) { setDisplayImage(undefined); return; }
    // A test design is loaded → the layered MOCKUP preview (design in the print area) owns the
    // display, ONCE IT HAS PAINTED ONE. Painting the raw matted product over a composed mockup
    // would hide the design — that's the "remove background → print area goes teal/grey, no
    // design" report, and it is why this used to bail unconditionally.
    //
    // But the mockup does not always arrive: no engine compose, a compose that fails, or a
    // restore/re-key that lands before the debounce. The unconditional bail left a gap where
    // NOBODY published `displayUrl`, so the rail said "background removed" while the canvas
    // (which draws `displayUrl ?? caseImageUrl`) still showed the untouched photo.
    //
    // So the rule is "do not CLOBBER a display", not "never paint": with nothing on screen the
    // keyed product goes up immediately, and the mockup replaces it when it composes.
    // `displayUrlRef`, not `displayUrl` — reading the state here would put it in the dep list
    // below, and this effect WRITES it, which is a render loop.
    if (testDesign && displayUrlRef.current !== undefined) return;
    const fg = editorRefs.sourceRef.current;
    if (!fg || typeof document === "undefined") return;
    const out = document.createElement("canvas");
    out.width = fg.width; out.height = fg.height;
    const ctx = get2dContext(out);
    if (!ctx) return;
    if (bgMode === "color") {
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, out.width, out.height);
    } else if (bgMode === "image" && bgImageRef.current) {
      try { ctx.drawImage(bgImageRef.current, 0, 0, out.width, out.height); } catch { /* ignore */ }
    }
    ctx.globalCompositeOperation = bgMode === "transparent" ? "source-over" : blendMode;
    try { ctx.drawImage(fg, 0, 0); } catch { /* ignore */ }
    ctx.globalCompositeOperation = "source-over";
    try { setDisplayImage(out.toDataURL("image/png")); } catch { /* tainted canvas */ }
    // MN-11 / review-1 M-3: the two split artefacts — a ref reached through the `editorRefs` bag
    // (stable identity, `editorContext.ts`) and a hook-returned setter (`useCallback`'d in
    // `useLayerAssets`). Both are stable, so naming them changes nothing about when this fires.
  }, [removalVer, bgMode, bgColor, blendMode, testDesign, editorRefs.sourceRef, setDisplayImage]);




  const active = cutouts[activeCutout];
  const activeRadius = active?.cornerRadius ?? 0;

  /* ── the rail's one-line section summaries (Direction B rule 8) ───────────────────────
   * A collapsed section must still answer "what is this set to?", or the accordion trades
   * a long column for a guessing game. Each line is the SAME vocabulary the section's own
   * controls use, so the head reads as a shorter version of the body rather than a second
   * name for it — "Stretch" is the `fill-mode` option's label, "print guide (auto)" is the
   * marker card's own default caption, "Phone case" is the product select's option. */
  const profileSummary = [
    PRODUCT_CATEGORY_LABEL[profileCategory],
    profileCategory === "phone_case" && profileClear ? "clear" : null,
    profileTexture ? (textureName || "texture") : null,
  ].filter(Boolean).join(" · ");
  const historySummary = canUndo || canRedo
    ? [canUndo ? "can undo" : null, canRedo ? "can redo" : null].filter(Boolean).join(" · ")
    : "no edits yet";
  // The saved-matte restore is IN FLIGHT: the canvas is showing "not removed" for a template
  // whose background WAS removed, and the head is the one line the operator reads with the
  // section folded. Saying "not removed" there was the silent half of the hang.
  const removalSummary = matteRestoring
    ? "restoring saved removal…"
    : removed
      ? `background removed · ${matteQuality}`
      : aiAvailable ? "not removed" : "not removed · desktop only";
  const designFitSummary = [
    FILL_FIT_LABEL[fillFit],
    designAspect ? `${designAspect[0]}:${designAspect[1]}` : "original aspect",
    designRotation ? `${designRotation}°` : null,
  ].filter(Boolean).join(" · ");
  const markerSummary = markerColor
    ? `${rgbToHexColor(markerColor)} · tolerance ${markerTol === 0 ? "auto" : markerTol}`
    : "print guide (auto)";
  const bgReplaceSummary = `${BG_MODE_LABEL[bgMode]}${blendMode === "source-over" ? "" : ` · ${blendMode}`}`;
  const cutoutSummary = [
    printArea.length >= 3 ? "print area set" : "no print area",
    cutouts.length === 0 ? "no cutouts" : cutouts.length === 1 ? "1 cutout" : `${cutouts.length} cutouts`,
  ].join(" · ");
  // F-5 (b) — the section is FOLDED by default, so "Invalid: …" inside it was invisible. The
  // head carries a warn dot whenever the geometry cannot be published, so the closed section
  // still says there is something in it to look at. The page-level banner
  // (`GeometryProblemBanner`, mounted by the host) is the signal; this is the pointer to it.
  const geometryWarn = printArea.length >= 3 && !validation.valid;

  /* ── WS-EXIF: does this view's stored geometry belong to the photo on the canvas? ─────────
   * A print-area polygon is bare 0..1 numbers with no record of the image that produced them.
   * That was fine until the frame moved, and it moves two ways: a browser applies a photo's EXIF
   * `Orientation` on decode and the engine did not (so a portrait phone shot was 720x1484 in one
   * and 1484x720 in the other), and replacing the reference image re-frames every polygon at
   * once. The old behaviour was to draw the numbers anyway — a rotated, oversized quad that
   * looks like a detection bug.
   *
   * `natDims` and not `w`/`h`: those fall back to 512 before the photo arrives, which would
   * accuse every view for a moment on mount. */
  const verdict = frameVerdict(props.authoringFrame ?? null, natDims);
  const frameBlocked = verdict !== "draw" && !frameResolved && printArea.length > 0;
  // The operator's answer is about ONE view's geometry against ONE photo.
  useEffect(() => { setFrameResolved(false); }, [props.view, props.caseImageUrl]);

  /** Turn every normalized polygon on the view a quarter-turn. The cutouts and the brand box are
   *  in the same frame as the print area, so turning only the print area would move the error
   *  somewhere less visible rather than fix it. */
  const turnGeometry = (turn: (p: Polygon) => Polygon, message: string) => {
    onPrintAreaChange(turn(printArea));
    onCutoutsChange(cutouts.map((c) => ({ ...c, polygon: turn(c.polygon) })));
    if (props.brandArea) props.onBrandAreaChange?.(turn(props.brandArea));
    setPlacementMsg(message);
  };

  /** Re-fit: turn the geometry when the photo is the same picture on its side, otherwise detect
   *  the print area again from the magenta marker. */
  const refitPrintArea = () => {
    if (verdict === "transpose") {
      turnGeometry(transposePolygon, "Print area turned to match this photo.");
    } else {
      removeByMagentaKey();
    }
    setFrameResolved(true);
    props.onAuthoringFrameResolved?.();
  };

  /**
   * Turn the other way.
   *
   * Reciprocal aspects say the picture is on its side; they do NOT say which way it was turned.
   * A phone held upright writes `Orientation = 6` and one held the other way writes `8`, and by
   * the time a template is reopened the tag is long gone. Re-fit guesses the common case; this
   * is the one click that fixes the other one, instead of leaving the operator to re-draw four
   * corners by hand.
   */
  const refitPrintAreaTheOtherWay = () => {
    turnGeometry(transposePolygonBack, "Print area turned the other way.");
    setFrameResolved(true);
    props.onAuthoringFrameResolved?.();
  };

  /** Keep as is: the operator asserts the stored quad is right for this photo. Draw it, and let
   *  the next Save record the current photo as its frame. */
  const keepPrintAreaAsIs = () => {
    setFrameResolved(true);
    props.onAuthoringFrameResolved?.();
  };

  return (
    <div
      ref={rootRef}
      className="mask-editor mask-editor--paneled"
      data-testid="mask-editor"
      // ED-14: pointer containment is one half of the arrow-key scope test (focus is the other).
      onPointerEnter={() => { pointerInsideRef.current = true; }}
      onPointerLeave={() => { pointerInsideRef.current = false; }}
    >
      {/* Direction B rule 8 — the right-hand column is ONE `.inspector` holding ONE
          `.accordion`. It used to be seven cards stacked open in a 360 px column: taller than
          any viewport, so the operator scrolled past four cards to reach the one they were
          using, and four of the seven had already grown their own `<details>` to cope (R12).
          Nothing is gone — every card below is the BODY of a section, the section heads carry a
          one-line summary of that card's current values, and the rail scrolls inside itself so
          a 900 px viewport never pushes the canvas off-screen (`.mask-editor__rail`). */}
      <div className="inspector mask-editor__rail mask-editor__controls">
        <div className="inspector__head">Controls</div>
        {locked && (
          <div className="mask-editor__locked-banner">
            This version is locked (Active). Editing is disabled.{" "}
            <button type="button" className="btn btn--sm" onClick={props.onCreateNewVersion}>
              Create new version
            </button>
          </div>
        )}
        {frameBlocked && (
          <div className="mask-editor__frame-banner" role="status" data-testid="frame-mismatch">
            <span>
              {FRAME_MISMATCH_MESSAGE}. {frameMismatchDetail(verdict as "transpose" | "redetect")}
            </span>
            <span className="mask-editor__frame-banner-actions">
              <button type="button" className="btn btn--sm" onClick={refitPrintArea} disabled={locked}>
                Re-fit
              </button>
              {verdict === "transpose" && (
                <button
                  type="button"
                  className="btn btn--sm"
                  onClick={refitPrintAreaTheOtherWay}
                  disabled={locked}
                >
                  Turn the other way
                </button>
              )}
              <button type="button" className="btn btn--sm" onClick={keepPrintAreaAsIs}>
                Keep as is
              </button>
            </span>
          </div>
        )}
        {/* `.mask-editor__chroma` stays on the body: it is what `.btn.is-active` in editor.css
            and the `.mask-editor__chroma-row` grid in components.css are scoped to, and every
            one of those buttons is still in here. `chroma-controls` is unchanged. */}
        <div className="inspector__body mask-editor__chroma" data-testid="chroma-controls">
        <Accordion
          name="mask-rail"
          openKey={railOpen}
          onOpenChange={setRailOpen}
          items={railItemsFor(props.railMode, [
            { key: "product", head: "Product", meta: railSummary(profileSummary), body: (
      <ProductProfileCard
        locked={locked}
        profileCategory={profileCategory}
        setProfileCategory={setProfileCategory}
        profileClear={profileClear}
        setProfileClear={setProfileClear}
        profileTexture={profileTexture}
        setProfileTexture={setProfileTexture}
        layerOpacity={layerOpacity}
        setLayerOpacity={setLayerOpacity}
        clearBg={clearBg}
        clearBgName={clearBgName}
        setClearBgName={setClearBgName}
        clearBgInputRef={clearBgInputRef}
        clearBgUrlRef={clearBgUrlRef}
        texture={texture}
        textureName={textureName}
        setTextureName={setTextureName}
        textureInputRef={textureInputRef}
        textureUrlRef={textureUrlRef}
        applyClearBg={applyClearBg}
        applyTexture={applyTexture}
        textureBlend={textureBlend}
        setTextureBlend={setTextureBlend}
        varnish={varnish}
        setVarnish={setVarnish}
        showVarnishSettings={showVarnishSettings}
        setShowVarnishSettings={setShowVarnishSettings}
        onProductImageFile={onProductImageFile}
        pickImageFromUrl={pickImageFromUrl}
        previewMockup={previewMockup}
        textureOptions={props.textureOptions}
        clearBgOptions={props.clearBgOptions}
      />

            ) },
            { key: "history", head: "Edit history", meta: railSummary(historySummary), body: (
      <HistoryCard
        locked={locked}
        canUndo={canUndo}
        canRedo={canRedo}
        canRevert={canRevert}
        undoGeo={undoGeo}
        redoGeo={redoGeo}
        revertGeo={revertGeo}
      />

            ) },
            /* Approved magenta flow: BiRefNet removes the background; the print area is keyed
               automatically from the synthetic magenta marker (no colour picking). Manual
               print-area / cutout tools live in their own section further down the rail. */
            { key: "bg-removal", head: "Remove background (AI)", meta: railSummary(removalSummary), body: (
        <AiRemovalCard
          locked={locked}
          engine={engine}
          editorRefs={editorRefs}
          aiAvailable={aiAvailable}
          aiBusy={aiBusy}
          aiElapsed={aiElapsed}
          aiStatus={aiStatus}
          segInfo={segInfo}
          compInfo={compInfo}
          chromaReady={chromaReady}
          removed={removed}
          matteRestoreFailed={matteRestoreFailed}
          matteRestoring={matteRestoring}
          retryMatteRestore={retryMatteRestore}
          autoRemoveBg={autoRemoveBg}
          cancelAutoRemoveBg={cancelAutoRemoveBg}
          removeByMagentaKey={removeByMagentaKey}
          resetBackground={resetBackground}
          matteKindRef={matteKindRef}
          matteQuality={matteQuality}
          setMatteQuality={setMatteQuality}
          protectProduct={protectProduct}
          setProtectProduct={setProtectProduct}
          showAiSettings={showAiSettings}
          setShowAiSettings={setShowAiSettings}
          aiEdge={aiEdge}
          setAiEdge={setAiEdge}
          aiEdgeKeep={aiEdgeKeep}
          setAiEdgeKeep={setAiEdgeKeep}
          edgeChoke={edgeChoke}
          setEdgeChoke={setEdgeChoke}
          edgeFeather={edgeFeather}
          setEdgeFeather={setEdgeFeather}
          matteStrength={matteStrength}
          setMatteStrength={setMatteStrength}
          stageDespill={stageDespill}
          setStageDespill={setStageDespill}
          stageRefine={stageRefine}
          setStageRefine={setStageRefine}
          stageMatteStrength={stageMatteStrength}
          setStageMatteStrength={setStageMatteStrength}
          stageShading={stageShading}
          setStageShading={setStageShading}
          printShadingStrength={printShadingStrength}
          setPrintShadingStrength={setPrintShadingStrength}
          shadeGradient={shadeGradient}
          setShadeGradient={setShadeGradient}
          shadeGrain={shadeGrain}
          setShadeGrain={setShadeGrain}
          shadeSheen={shadeSheen}
          setShadeSheen={setShadeSheen}
          shadeHighlights={shadeHighlights}
          setShadeHighlights={setShadeHighlights}
          shadeShadowDepth={shadeShadowDepth}
          setShadeShadowDepth={setShadeShadowDepth}
          shadeHighlightDepth={shadeHighlightDepth}
          setShadeHighlightDepth={setShadeHighlightDepth}
          shadeInkBlack={shadeInkBlack}
          setShadeInkBlack={setShadeInkBlack}
          shadeInkWhite={shadeInkWhite}
          setShadeInkWhite={setShadeInkWhite}
        />

            ) },
            { key: "design-fit", head: "Design fit & aspect", meta: railSummary(designFitSummary), body: (
        <DesignFitCard
          locked={locked}
          chromaReady={chromaReady}
          fillFit={fillFit}
          setFillFit={setFillFit}
          designAspect={designAspect}
          setDesignAspect={setDesignAspect}
          designRotation={designRotation}
          setDesignRotation={setDesignRotation}
          testDesign={testDesign}
          testDesignName={testDesignName}
          testDesignInputRef={testDesignInputRef}
          onTestDesignFile={onTestDesignFile}
          clearTestDesignNow={clearTestDesignNow}
          previewMockup={previewMockup}
          setRemovalVer={setRemovalVer}
          // A customer's test design is a preview: the wizard has no template to keep it with.
          testDesignSaved={props.railMode !== "placement"}
        />
            ) },
            /* Edge calibration + AI edge live in the "AI settings" gear popup inside the
               Background-removal section above (relocated, not duplicated) — ai-settings-panel. */
            { key: "marker", head: "Marker & keying", meta: railSummary(markerSummary), body: (
        <MarkerKeyingCard
          locked={locked}
          chromaReady={chromaReady}
          markerColor={markerColor}
          setMarkerColor={setMarkerColor}
          markerTol={markerTol}
          setMarkerTol={setMarkerTol}
          markerEyedrop={markerEyedrop}
          setMarkerEyedrop={setMarkerEyedrop}
          keyedPct={keyedPct}
          pickNotice={pickNotice}
          guideKey={guideKey}
          setGuideKey={setGuideKey}
          cameraShading={props.cameraShading}
        />

            ) },
            ...(removalVer > 0 && !locked
              ? [{ key: "bg-replace", head: "Background replace", meta: railSummary(bgReplaceSummary), body: (
        <BackgroundReplaceCard
          locked={locked}
          removalVer={removalVer}
          setRemovalVer={setRemovalVer}
          bgMode={bgMode}
          setBgMode={setBgMode}
          bgColor={bgColor}
          setBgColor={setBgColor}
          blendMode={blendMode}
          setBlendMode={setBlendMode}
          bgImageRef={bgImageRef}
          onBgImageFile={onBgImageFile}
        />
                ) }]
              : []),
            { key: "cutouts", head: "Print area & cutouts", meta: (
              <span className="mask-editor__rail-summary">
                {geometryWarn && (
                  <span
                    className="mask-editor__rail-warn"
                    data-testid="cutouts-warn-dot"
                    title="This geometry cannot be published — see the banner at the top of the page."
                  >
                    <Icon name="warning" size={16} />
                  </span>
                )}
                {cutoutSummary}
              </span>
            ), body: (
      <CutoutToolsCard
        locked={locked}
        printArea={printArea}
        cutouts={cutouts}
        active={active}
        activeRadius={activeRadius}
        activeCutout={activeCutout}
        validation={validation}
        addCutout={addCutout}
        addShapeCutout={addShapeCutout}
        deleteActiveCutout={deleteActiveCutout}
        seedPrintArea={seedPrintArea}
        smoothActiveCutout={smoothActiveCutout}
        adjustCornerRadius={adjustCornerRadius}
      />
            ) },
          ], props.adminRailItems)}
        />
        </div>{/* /inspector body */}
      </div>{/* /the rail */}

      {/* Preview column (spec 019 US3) — the live composite + handles. WS-J J-11: the zoom bar,
          the <svg> stage, every handle and the legend live in `MaskCanvas`, which takes the zoom
          (J-4) and geometry (J-10) hooks' output as PROPS so it is renderable without the editor. */}
      <MaskCanvas
        w={w} h={h}
        zoom={zoom} setZoom={setZoom} center={center} setCenter={setCenter}
        vb={vb} hr={hr} hitr={hitr} sw={sw} zoomBy={zoomBy} resetZoom={resetZoom} svgRef={svgRef}
        displayUrl={displayUrl}
        caseImageUrl={props.caseImageUrl}
        underlayUrl={clearUnderlayUrl}
        printArea={/* WS-EXIF: geometry from another frame is NOT drawn — showing it (and its drag
          handles) would present a wrong placement as if it were the real one. The banner above
          says so and offers the re-fit; until then the canvas shows the photo alone. The BRAND
          box goes with them: it is normalized against the same frame, Re-fit turns it with the
          rest, and leaving it on screen would invite the operator to drag it into place against
          a quad they cannot see. */
          frameBlocked ? EMPTY_GEOMETRY : printArea}
        cutouts={frameBlocked ? EMPTY_CUTOUTS : cutouts}
        effective={frameBlocked ? EMPTY_GEOMETRY : effective}
        brandArea={frameBlocked ? null : props.brandArea}
        onBrandAreaChange={props.onBrandAreaChange}
        printAreaActive={printAreaActive} setPrintAreaActive={setPrintAreaActive}
        activeCutout={activeCutout} setActiveCutout={setActiveCutout}
        selectedPrintVertex={selectedPrintVertex} setSelectedPrintVertex={setSelectedPrintVertex}
        drag={drag} setDrag={setDrag}
        toNorm={toNorm} onPointerMove={onPointerMove} endDrag={endDrag}
        onBackgroundClick={onBackgroundClick} deleteVertex={deleteVertex}
        locked={locked}
        railMode={props.railMode}
        markerEyedrop={markerEyedrop}
        removed={removed}
        bgMode={bgMode}
        /* WS-ERR F-2 — the compose's operator-facing refusals (`sayWhyNot`) and the placement
           detector's status used to render inside `AiRemovalCard`, i.e. in the BODY of the
           rail's "Remove background (AI)" section. The rail is an exclusive accordion and the
           operator presses "Preview mock-up" from "Design fit & aspect", so every one of those
           sentences landed in a drawer that was shut at that exact moment. It lives in the
           preview column now — same testid, same role, same words, beside the canvas. */
        statusSlot={
          placementMsg ? (
            <span className="mask-editor__chroma-status" role="status" data-testid="placement-status">
              {placementMsg}
            </span>
          ) : null
        }
      />
    </div>
  );
}
